import { z } from 'zod';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';
import { decCmp, decSub, decSum, formatDec } from './decimal.js';
import {
  optAmount,
  optDay,
  optEnum,
  optId,
  optLocalTime,
  optNote,
  optText,
  requiredIssues,
  resultOf,
  typed,
} from './fields.js';

/**
 * Yakıt alımı (ortak belgesi §7, ekran 03; ADR 0117 §3): operasyon, tedarik ve BDN, yakıt ve
 * kalite, üç ayrı miktar (BDN, gemi ölçümü, kabul), tank dağılımı, numune ve analiz, maliyet.
 * Taslak eksik kaydedilir; gönderimde zorunlular ve kurallar (`submitIssues`). Kayıt tek yakıt
 * türüdür (her ürünün kendi BDN'i; ADR 0117 Ek Y2). Saf modül.
 */

export const DELIVERY_METHODS = ['barge', 'truck', 'pipeline', 'ship_to_ship'] as const;
export const SAMPLE_TYPES = ['marpol', 'supplier', 'ship', 'lab'] as const;
/** Bir alımda en çok tank satırı. */
export const BUNKERING_MAX_LINES = 30;
/** Yoğunluk sınırı (t/m³, 15 °C): ISO 8217 en yüksek değerin çok üstü; yazım hatasını yakalar. */
export const DENSITY_MAX = '1.2';

const lineSchema = z.strictObject({
  tankId: z.uuid('INVALID_ID'),
  massMt: optAmount({ positive: true }),
  remarks: optText(200),
});

const alternativeSchema = z
  .strictObject({
    component: optText(80),
    ratioPct: optAmount({ max: '100' }),
    origin: optText(120),
    certificate: optText(120),
    sustainabilityEvidence: optText(200),
  })
  .nullish()
  .transform((value) => (value && Object.values(value).some((v) => v !== null) ? value : null));

export const fuelBunkeringSchema = z.strictObject({
  // Operasyon
  portName: optText(120),
  portCode: optText(12),
  voyageId: optId,
  /** Sefer bağı yoksa açıklaması (ortak belgesi §7 "Bağlantı"). */
  unlinkedReason: optText(300),
  startedLocal: optLocalTime,
  endedLocal: optLocalTime,
  responsibleId: optId,
  deliveryMethod: optEnum(DELIVERY_METHODS),
  // Tedarik ve belge
  supplier: optText(120),
  barge: optText(120),
  bdnNo: optText(60),
  bdnDate: optDay,
  // Yakıt ve kalite
  fuelTypeId: optId,
  grade: optText(40),
  /** Parti kimliği; boşsa BDN numarası. */
  batchCode: optText(60),
  density15: optAmount({ positive: true, max: DENSITY_MAX }),
  sulphurPct: optAmount({ max: '100' }),
  viscosityCst: optAmount(),
  flashPointC: optAmount(),
  // Miktar: birbirinin üzerine yazılmaz
  bdnMt: optAmount({ positive: true }),
  shipMt: optAmount({ positive: true }),
  acceptedMt: optAmount({ positive: true }),
  lines: z
    .array(lineSchema, typed('INVALID_LIST'))
    .max(BUNKERING_MAX_LINES, 'TOO_LONG')
    .nullish()
    .transform((value) => value ?? []),
  // Numune ve analiz
  sampleNo: optText(60),
  sealNo: optText(60),
  sampleType: optEnum(SAMPLE_TYPES),
  sampleLocation: optText(120),
  laboratory: optText(120),
  analysisResult: optText(300),
  alternative: alternativeSchema,
  // Maliyet: kur ve kur tarihi ayrı
  unitPrice: optAmount(),
  currency: z
    .string(typed('INVALID_CURRENCY'))
    .regex(/^[A-Z]{3}$/, 'INVALID_CURRENCY')
    .nullish()
    .transform((value) => value ?? null),
  extraCost: optAmount(),
  invoiceNo: optText(60),
  fxRate: optAmount({ positive: true }),
  fxDate: optDay,
  // Fark ve açıklama
  differenceNote: optNote(500),
  remarks: optNote(500),
});
export type FuelBunkeringInput = z.output<typeof fuelBunkeringSchema>;

/** Gönderimde zorunlu alanlar (ekran 03'ün yıldızlıları; analiz ve fatura sonradan eklenebilir). */
export const BUNKERING_REQUIRED = [
  'portName',
  'startedLocal',
  'endedLocal',
  'responsibleId',
  'supplier',
  'barge',
  'bdnNo',
  'bdnDate',
  'fuelTypeId',
  'density15',
  'sulphurPct',
  'bdnMt',
  'shipMt',
  'acceptedMt',
  'lines',
  'sampleNo',
  'sealNo',
  'unitPrice',
  'currency',
] as const satisfies readonly (keyof FuelBunkeringInput)[];

export function parseFuelBunkering(input: unknown): ParseResult<FuelBunkeringInput> {
  const result = resultOf(fuelBunkeringSchema.safeParse(input));
  if (!result.ok) {
    // Satır sorunu satırın alanıdır (`lines.0.massMt`)
    return result;
  }
  const tanks = result.value.lines.map((line) => line.tankId);
  if (new Set(tanks).size !== tanks.length) {
    return { ok: false, issues: [{ path: 'lines', code: 'DUPLICATE_TANK' }] };
  }
  return result;
}

/** Tank dağılımının toplamı (MT). */
export function distributionTotal(lines: readonly { massMt: string | null }[]): string {
  return formatDec(decSum(lines.map((line) => line.massMt ?? '0')));
}

/** BDN − kabul farkı (MT); farkı kapatmak için tüketim üretilmez (ortak belgesi §7). */
export function bdnDifference(input: {
  bdnMt: string | null;
  acceptedMt: string | null;
}): string | null {
  if (input.bdnMt === null || input.acceptedMt === null) return null;
  return formatDec(decSub(input.bdnMt, input.acceptedMt));
}

/**
 * Gönderim kuralları: zorunlular; sefer yoksa açıklaması; bitiş başlangıçtan önce olamaz;
 * dağılım toplamı kabul miktarına eşit (`DISTRIBUTION_MISMATCH`); her satırın miktarı var.
 */
export function bunkeringSubmitIssues(value: FuelBunkeringInput): SchemaIssue[] {
  const issues = requiredIssues(value, BUNKERING_REQUIRED);
  if (value.voyageId === null && value.unlinkedReason === null) {
    issues.push({ path: 'unlinkedReason', code: 'REQUIRED' });
  }
  if (value.startedLocal && value.endedLocal && value.endedLocal < value.startedLocal) {
    issues.push({ path: 'endedLocal', code: 'END_BEFORE_START' });
  }
  value.lines.forEach((line, index) => {
    if (line.massMt === null) issues.push({ path: `lines.${index}.massMt`, code: 'REQUIRED' });
  });
  if (
    value.acceptedMt !== null &&
    value.lines.length > 0 &&
    value.lines.every((line) => line.massMt !== null) &&
    decCmp(distributionTotal(value.lines), value.acceptedMt) !== 0
  ) {
    issues.push({ path: 'lines', code: 'DISTRIBUTION_MISMATCH' });
  }
  return issues;
}
